import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

const PROGRAM_COLOR = {
  slate:   { bg: '#F1EFE8', color: '#5F5E5A', solid: '#5F5E5A' },
  blue:    { bg: '#E6F1FB', color: '#185FA5', solid: '#378ADD' },
  violet:  { bg: '#F3E8FF', color: '#6B21A8', solid: '#7C3AED' },
  emerald: { bg: '#EAF3DE', color: '#3B6D11', solid: '#65A30D' },
  amber:   { bg: '#FAEEDA', color: '#854F0B', solid: '#D97706' },
}

export default function Home() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)
  const [recentAudits, setRecentAudits] = useState([])
  const [programs, setPrograms] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchDashboard() }, [])

  async function fetchDashboard() {
    const [auditsRes, answersRes, progsRes, progStatsRes] = await Promise.all([
      supabase.from('audits').select('id, status, created_at, sites(name)').order('created_at', { ascending: false }).limit(5),
      supabase.from('audit_answers').select('result'),
      supabase.from('programs').select('*').order('sort_order'),
      supabase.from('program_stats').select('*'),
    ])
    setRecentAudits(auditsRes.data || [])
    const answers = answersRes.data || []
    setStats({
      total: answers.length,
      pass:  answers.filter(a => a.result === 'pass').length,
      fail:  answers.filter(a => a.result === 'fail').length,
      na:    answers.filter(a => a.result === 'na').length,
    })
    // merge program stats
    const statsById = {}
    ;(progStatsRes.data || []).forEach(s => { statsById[s.id] = s })
    setPrograms((progsRes.data || []).map(p => ({ ...p, stats: statsById[p.id] || {} })))
    setLoading(false)
  }

  if (loading) return <div className="spinner" />

  const role = profile?.role
  const isAdmin = role === 'admin'
  const isExec  = role === 'executive'
  const isOps   = isAdmin || isExec || role === 'regional_ops'

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>
          Welcome{profile?.full_name ? `, ${profile.full_name.split(' ')[0]}` : ''}
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>
          {isExec ? 'Executive view — three programs, real-time progress.' : 'CarWash Audit Portal'}
        </p>
      </div>

      {/* Programs summary cards — Exec / Admin / Regional see this up top */}
      {isOps && programs.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            Programs
          </div>
          {programs.map(p => {
            const color = PROGRAM_COLOR[p.color] || PROGRAM_COLOR.slate
            const s = p.stats || {}
            return (
              <div
                key={p.id}
                className="card"
                style={{ marginBottom: 8, cursor: 'pointer', padding: 14 }}
                onClick={() => navigate(`/programs/${p.slug}`)}
              >
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 8, background: color.solid,
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700, flexShrink: 0,
                  }}>
                    {p.slug === 'pa-phase1' ? 'PA' : p.slug === 'amp-migration' ? 'AMP' : 'MAIN'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      {s.site_count || 0} sites · {s.open_ticket_count || 0} open
                      {s.critical_open > 0 && <span style={{ color: 'var(--red)', marginLeft: 4 }}>· {s.critical_open} critical</span>}
                    </div>
                  </div>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
                    style={{ width: 16, height: 16, color: 'var(--text-muted)', flexShrink: 0 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Admin-only shortcut to dashboard */}
      {isAdmin && (
        <div
          className="card"
          style={{ marginBottom: 16, cursor: 'pointer', borderColor: 'var(--blue-mid)', background: 'var(--blue-light)' }}
          onClick={() => navigate('/dashboard')}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--blue)' }}>📊 Admin Dashboard</div>
              <div style={{ fontSize: 13, color: 'var(--blue)', opacity: 0.8, marginTop: 2 }}>
                Cross-site summary, failures by category, site progress
              </div>
            </div>
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
              style={{ width: 18, height: 18, color: 'var(--blue)', flexShrink: 0 }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      )}

      {stats && (
        <div className="stats-grid">
          <div className="stat-card"><div className="stat-number">{stats.total}</div><div className="stat-label">Total answers</div></div>
          <div className="stat-card"><div className="stat-number" style={{ color: 'var(--green)' }}>{stats.pass}</div><div className="stat-label">Passed</div></div>
          <div className="stat-card"><div className="stat-number" style={{ color: 'var(--red)' }}>{stats.fail}</div><div className="stat-label">Failed</div></div>
          <div className="stat-card"><div className="stat-number" style={{ color: 'var(--amber)' }}>{stats.na}</div><div className="stat-label">N/A</div></div>
        </div>
      )}

      {!isExec && (
        <div style={{ marginBottom: 20 }}>
          <button
            className="btn btn-primary"
            style={{ width: '100%', padding: 14, fontSize: 16 }}
            onClick={() => navigate('/audits/new')}
          >
            + Start New Audit
          </button>
        </div>
      )}

      <div>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Recent audits</h2>
        {recentAudits.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: 14 }}>
            No audits yet. Start your first one above!
          </div>
        ) : (
          recentAudits.map(audit => (
            <div key={audit.id} className="site-item" onClick={() => navigate(`/audits/${audit.id}`)}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 15 }}>{audit.sites?.name || 'Unknown site'}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{new Date(audit.created_at).toLocaleDateString()}</div>
              </div>
              <span className={`badge ${audit.status === 'submitted' ? 'badge-pass' : 'badge-warn'}`}>
                {audit.status === 'submitted' ? 'Submitted' : 'In progress'}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
